import { Context } from '../..';
import { SortByOption } from '../../../enums/sort-by-option';
import { Skill } from '../../../models/refactored/skill';
import { NotificationPriority, NotificationType } from '../../../notifications/notifications.models';
import * as base from '../base';

// models
export interface SearchSkillCategoryListRequest {
  search?: string;
  sortBy?: SortByOption;
  page?: number;
  pageSize?: number;
  isArchived?: boolean;
}

export interface SearchSkillCategoryListItemResponse {
  slug: string;
  name: string;
  description: string | undefined;
  isArchived: boolean;
  skillCount: number;
}

export interface SearchSkillCategoryListResponse {
  totalPages: number;
  results: SearchSkillCategoryListItemResponse[];
}

export interface CreateSkillCategoryRequest {
  name: string;
  description: string | undefined;
  skillSlugs: string[];
}

export interface UpdateSkillCategoryRequest extends CreateSkillCategoryRequest {
  skillCategorySlug: string;
}

type GetSkillCategorySkillResponse = Skill;

export interface GetSkillCategoryResponse {
  slug: string;
  name: string;
  description: string | undefined;
  isArchived: boolean;
  skills: GetSkillCategorySkillResponse[];
}

// actions
const CONTROLLER = `skillcategory`;

export async function searchSkillCategories({ state }: Context, payload: SearchSkillCategoryListRequest): Promise<SearchSkillCategoryListResponse | undefined> {
  const request: base.IRequest = { url: base.apiURL(`${CONTROLLER}${base.params({ companySlug: state.companyVariables.slug, ...payload })}`) };
  const response: base.IResponse<SearchSkillCategoryListResponse> = await base.request(request);
  return response.data;
}

export async function createSkillCategory({ state, actions }: Context, payload: CreateSkillCategoryRequest): Promise<string | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER), method: 'POST', body: JSON.stringify({ companySlug: state.companyVariables.slug, ...payload }) };
  const response: base.IResponse<string> = await base.request(request);

  if (!!response.error) {
    actions.addNotification({
      id: crypto.randomUUID(),
      active: true,
      type: NotificationType.Error,
      title: response.error.title,
      message: response.error.detail,
      priority: NotificationPriority.Toast,
    });
  }

  return response.data;
}

export async function readSkillCategory({ state }: Context, payload: string): Promise<GetSkillCategoryResponse | undefined> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `${payload}${base.params({ companySlug: state.companyVariables.slug })}`) };
  const response: base.IResponse<GetSkillCategoryResponse> = await base.request(request);
  return response.data;
}

export async function updateSkillCategory({ state }: Context, payload: UpdateSkillCategoryRequest): Promise<void> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER), method: 'PUT', body: JSON.stringify({ companySlug: state.companyVariables.slug, ...payload }) };
  await base.request(request);
}

export async function deleteSkillCategory({ state }: Context, payload: string): Promise<void> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `${payload}${base.params({ companySlug: state.companyVariables.slug })}`), method: 'DELETE' };
  await base.request(request);
}

export async function archiveSkillCategory({ state }: Context, payload: string): Promise<void> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `archive`), method: 'POST', body: JSON.stringify({ companySlug: state.companyVariables.slug, skillCategorySlug: payload }) };
  await base.request(request);
}

export async function unarchiveSkillCategory({ state }: Context, payload: string): Promise<void> {
  const request: base.IRequest = { url: base.apiURL(CONTROLLER, `un-archive`), method: 'POST', body: JSON.stringify({ companySlug: state.companyVariables.slug, skillCategorySlug: payload }) };
  await base.request(request);
}
